import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { checkInputErrorRequired, useInput, usePathParam, useSelect, useViewport } from '@chilecompra/react-kit'
import { Button, Card, Input, Select, Toast, Typography } from '@chilecompra/react-kit/components'
import { onGetPOBiddingInfoThunk, onSetFormData } from './Sanction.actions'
import { TYPE } from '../../config/settings/constant'
import { Box, Column, Row } from './SanctionPOBidding.styles'
import { REACT_APP_FEATURE_FLAG_PO_BIDDING } from '../../config/settings/environment'

/**
 * The Saction purchase order section container.
 */
const SanctionPurchaseOrder = () => {
  const dispatch = useDispatch()
  const {
    size: { isSmall, isTiny, isUpToMedium }
  } = useViewport()
  const dni = usePathParam('rut')
  const sanctionID = usePathParam('id')
  const {
    poBiddingInfo: { acceptanceDate, closingDate, code, error, loading, name, openingDate, shippingDate, processType },
    userInfo: { error: userInfoError }
  } = useSelector(state => state.sanction)
  const { error: sellerError, orgLegalName } = useSelector(state => state.basicSellerInformation)

  const {
    error: poBiddingError,
    value: poBiddingValue,
    onChange: handlePoBiddingChange
  } = useInput({
    initialValue: code,
    errorCallbacks: [checkInputErrorRequired()],
    overwriteCallback: () => code
  })

  const {
    error: processTypeError,
    onChange: handleProcessTypeChange,
    value: processTypeValue
  } = useSelect({
    errorCallbacks: [checkInputErrorRequired()],
    initialValue:
      REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true' ? processType : 'PO',
    overwriteCallback: () =>
      REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true' ? processType : 'PO'
  })

  const handleSearchPO = event => {
    event.preventDefault()
    dispatch(onGetPOBiddingInfoThunk({ externalCode: poBiddingValue, dni, processType: processTypeValue }))
  }

  const handleCleanPO = event => {
    event.preventDefault()
    dispatch(onSetFormData())
  }

  const disableSearchPOBid =
    ((REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true') &&
      processTypeValue === '' &&
      code === '' &&
      loading) ||
    ((REACT_APP_FEATURE_FLAG_PO_BIDDING === false || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'false') &&
      code === '' &&
      loading)

  return userInfoError || sellerError ? null : (
    <Row margin="0 0 2rem">
      <Column xs={12} sm={12} md={9} padding={isUpToMedium ? '0 27px 0 0' : '0'}>
        <Typography fontWeight="bold" variant="h3">
          Detalle de la sanción
        </Typography>
        <Box alignItems="center" display={isTiny ? 'block' : 'flex'} margin="28px 0 0">
          {(REACT_APP_FEATURE_FLAG_PO_BIDDING === true || REACT_APP_FEATURE_FLAG_PO_BIDDING === 'true') && (
            <Box margin="0 20px 0 0" width={isTiny ? '100%' : '240px'}>
              <Select
                disabled={!isNaN(sanctionID) || (code !== '' && !error)}
                error={processTypeError}
                label="Tipo de proceso"
                onChange={handleProcessTypeChange}
                options={TYPE || []}
                value={processTypeValue}
              />
            </Box>
          )}
          <Box margin="0 20px 0 0" width={isTiny ? '100%' : '340px'}>
            <Input
              disabled={!isNaN(sanctionID) || (code !== '' && !error)}
              error={poBiddingError}
              info={isNaN(sanctionID) && 'Ejemplo: 1234-12-CM20'}
              label="Código externo"
              value={poBiddingValue}
              onChange={handlePoBiddingChange}
            />
          </Box>

          {isNaN(sanctionID) && (code === '' || error || (code !== '' && loading)) && (
            <Button
              color="primary"
              loading={code !== '' && loading}
              disabled={disableSearchPOBid}
              variant="outlined"
              onClick={handleSearchPO}
            >
              Ingresar
            </Button>
          )}

          {isNaN(sanctionID) && code !== '' && !error && !loading && (
            <Button color="primary" variant="outlined" width="260px" onClick={handleCleanPO}>
              Limpiar
            </Button>
          )}
        </Box>

        {processTypeValue === 'BID' && (
          <Toast margin="32px 0 0" severity="info">
            Para un próximo proceso sancionatorio, te sugerimos aplicar la sanción a una orden de compra por sobre una
            licitación. Esto permite que el cálculo de la nota del proveedor sea más preciso.
          </Toast>
        )}

        {error && !loading && error?.reason !== 'SOMETHING_WENT_WRONG_ERROR' && (
          <Toast severity="warning" margin="32px 0 0">
            {error.reason === 'PO_DOES_NOT_EXIST_ERROR' &&
              `No existe una ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } asociada al número que indicas. Por favor, revisa los datos ingresados e inténtalo nuevamente. `}
            {error.reason === 'DOES_NOT_BELONG_SELLER_ERROR' &&
              `La ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } ingresada no fue emitida al proveedor ${orgLegalName}. Por favor, ingresa a la ficha del provedor correspondiente para poder registrar la sanción correctamente.`}
            {error.reason === 'DOES_NOT_BELONG_BUYER_ERROR' &&
              `La ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } ingresada no fue emitida por el organismo público al que perteneces. No puedes registrar sanciones por ${
                processTypeValue === 'PO' ? 'órdenes de compra' : 'licitaciones'
              } que no fueron emitidas por tu institución.`}
            {error.reason === 'IS_NOT_ACCEPTED_STATUS_ERROR' &&
              `Esta ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } no ha sido aceptada por el proveedor. Para poder registrar una sanción, la ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } debe estar primero en estado ACEPTADA o posterior.`}
            {error.reason === 'AMOUNT_MUST_BE_LESS_THAN_1000_ERROR' &&
              `Esta ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } es mayor a 1000 UTM. Para registrar una sanción por incumplimiento, debes primero crear una ficha de contrato, y generar la sanción respectiva desde esa misma ficha, en la sección sanciones. Una vez registrada, se verá en la ficha del proveedor sancionado de forma automática.`}
            {error.reason === 'PO_HAS_CONTRACT_ERROR' &&
              `Para registrar una sanción por incumplimiento a la ${
                processTypeValue === 'PO' ? 'orden de compra' : 'licitación'
              } ${poBiddingValue}, debes hacerlo desde su ficha de contrato, desde la opción Contratos del menú de Mercado Público.`}
            {error.reason === 'BOSS_ENTER_SANCTION_CM_ERROR' &&
              'La licitación pertenece a un Convenio Marco. El ingreso de sanciones solo puede ser realizada por el Jefe de Productos.'}
          </Toast>
        )}

        {error && !loading && error?.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
          <Toast
            margin="32px 0 0"
            severity="error"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor."
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        )}

        {!loading && !error && (
          <Card color="default" padding="24px 26px" margin="28px 0 0">
            <Row>
              <Column md={6} sm={6} xs={6}>
                <Typography fontWeight="bold" variant="body2">
                  {processTypeValue === 'PO' ? 'Nombre orden de compra' : 'Nombre licitación'}
                </Typography>
                <Typography variant="body2">{name}</Typography>
              </Column>
              <Column md={3} sm={3} xs={6}>
                <Typography fontWeight="bold" variant="body2">
                  {processTypeValue === 'PO' ? 'Fecha de envío' : 'Fecha apertura'}
                </Typography>
                <Typography variant="body2">{processTypeValue === 'PO' ? shippingDate : openingDate}</Typography>
              </Column>
              <Column md={3} sm={3} xs={6} padding={!isUpToMedium && !isSmall ? '18px 5px 0' : '0 5px'}>
                <Typography fontWeight="bold" variant="body2">
                  {processTypeValue === 'PO' ? 'Fecha de aceptación' : 'Fecha cierre'}
                </Typography>
                <Typography variant="body2">{processTypeValue === 'PO' ? acceptanceDate : closingDate}</Typography>
              </Column>
            </Row>
          </Card>
        )}
      </Column>
    </Row>
  )
}

export default SanctionPurchaseOrder
